/* prismoon 2026-09-21: sortable Size / Neck Size / Outer Dimensions columns on the quick-order tables
   (collection sections + search section). Self-contained: it only reads the rendered table and reorders
   the rows, never touching the QO core scripts. Runs only on single-page tables (a paginated table would
   sort one page of many). Sold-out rows stay at the bottom; the third click restores the table's own order.
   Revert: remove this script tag from the two sections (theme-backups/qo-sort-20260921).
   2026-10-15: Item No column added. Its header is data-col="image" and the cell also holds the picture and a
   hidden title, so the value comes from the row's data-item-no (510101-17 -> [510101, 17]). */
(() => {
  const ROOT = '[data-airgrilles-qo3]';
  const ROW = '[data-airgrilles-qo3-row]';
  const PARAM = 'qo3-sort';
  // key: url/state name, col: header data-col, attr: row data-* to read instead of the cell text
  const COLS = [
    { key: 'item_no', col: 'image', attr: 'itemNo', label: 'Item No', asc: 'low to high', desc: 'high to low' },
    { key: 'size', label: 'Size' },
    { key: 'neck_size', label: 'Neck Size' },
    { key: 'outer_dimensions', label: 'Outer Dimensions' }
  ].map((col) => Object.assign({ col: col.key, asc: 'small to large', desc: 'large to small' }, col));
  // up/down triangles; the active direction is solid, the other faint. Explicit size because the theme
  // shrinks inline svg (svg { width: 1rem })
  function arrowSvg(dir) {
    const up = dir === 'asc' ? 1 : dir === 'desc' ? 0.2 : 0.5;
    const down = dir === 'desc' ? 1 : dir === 'asc' ? 0.2 : 0.5;
    return '<svg viewBox="0 0 10 14" width="10" height="14" aria-hidden="true" focusable="false">' +
      '<path d="M5 1L9.5 6H.5z" fill="currentColor" opacity="' + up + '"/>' +
      '<path d="M5 13L.5 8h9z" fill="currentColor" opacity="' + down + '"/></svg>';
  }

  function injectStyle() {
    if (document.getElementById('qo3-sort-style')) return;
    const style = document.createElement('style');
    style.id = 'qo3-sort-style';
    style.textContent =
      '.qo3-sortable{cursor:pointer;user-select:none;}' +
      '.qo3-sortable:hover,.qo3-sortable:focus-visible{background:rgba(0,0,0,.05);outline:none;}' +
      '.qo3-sort-ind{display:inline-block;margin-left:6px;vertical-align:-2px;line-height:0;}' +
      '.qo3-sort-ind svg{width:10px !important;height:14px !important;display:block;}' +
      '.qo3-sort-pill{display:none !important;}' +
      '@media (max-width:768px){.qo3-sort-pill{display:inline-flex !important;}}';
    document.head.appendChild(style);
  }

  // numbers in the order they appear, fractions included: 3-3/8"W X 11-3/8"H -> [3.375, 11.375]
  function numbers(text) {
    const out = [];
    const re = /(\d+(?:\.\d+)?)\s*-\s*(\d+)\s*\/\s*(\d+)|(\d+)\s*\/\s*(\d+)|(\d+(?:\.\d+)?)/g;
    let m;
    while ((m = re.exec(String(text || '')))) {
      if (m[1]) out.push(Number(m[1]) + Number(m[2]) / Number(m[3]));
      else if (m[4]) out.push(Number(m[4]) / Number(m[5]));
      else out.push(Number(m[6]));
    }
    return out;
  }

  function compareKeys(a, b) {
    for (let i = 0; i < Math.min(a.length, b.length); i += 1) {
      if (a[i] !== b[i]) return a[i] - b[i];
    }
    return a.length - b.length;
  }

  function cellText(row, col) {
    if (col.attr) return String(row.dataset[col.attr] || '').trim();
    const cell = row.querySelector('td[data-col="' + col.col + '"]');
    return cell ? cell.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function header(root, col) {
    return root.querySelector('thead th[data-col="' + col.col + '"]');
  }

  function readState() {
    const raw = new URL(window.location.href).searchParams.get(PARAM) || '';
    const m = raw.match(/^([a-z_]+)-(asc|desc)$/);
    return m ? { key: m[1], dir: m[2] } : { key: '', dir: '' };
  }

  function writeState(state) {
    const url = new URL(window.location.href);
    if (state.key) url.searchParams.set(PARAM, state.key + '-' + state.dir);
    else url.searchParams.delete(PARAM);
    history.replaceState(history.state, '', url.toString());
  }

  function init(root) {
    if (root.__qo3Sort) return;
    root.__qo3Sort = true;
    if (root.querySelector('.airgrilles-qo3__pagination')) return; // paginated: sorting one page would mislead
    const rows = Array.from(root.querySelectorAll(ROW));
    if (rows.length < 2) return;
    const tbody = rows[0].parentElement;
    rows.forEach((row, i) => { row.dataset.qo3Default = String(i); });

    const cols = COLS.filter((col) => {
      const th = header(root, col);
      if (!th || th.classList.contains('is-col-hidden')) return false;
      const values = new Set(rows.map((row) => cellText(row, col)).filter(Boolean));
      return values.size > 1;
    });
    if (!cols.length) return;
    injectStyle();

    const soldOutBottom = (root.dataset.soldOutBehavior || 'bottom') !== 'inline';
    let state = { key: '', dir: '' };
    let select = null;

    function apply(next) {
      const active = cols.find((col) => col.key === next.key);
      state = active ? next : { key: '', dir: '' };
      const items = Array.from(root.querySelectorAll(ROW)).map((row) => ({
        row,
        avail: row.dataset.available === 'true',
        def: Number(row.dataset.qo3Default || 0),
        k: active ? numbers(cellText(row, active)) : []
      }));
      const sign = state.dir === 'desc' ? -1 : 1;
      items.sort((a, b) => {
        if (soldOutBottom && a.avail !== b.avail) return a.avail ? -1 : 1;
        if (state.key) {
          if (!a.k.length !== !b.k.length) return a.k.length ? -1 : 1; // no value: always last
          const c = compareKeys(a.k, b.k) * sign;
          if (c) return c;
        }
        return a.def - b.def;
      });
      items.forEach((item) => tbody.appendChild(item.row));
      cols.forEach((col) => {
        const th = header(root, col);
        const on = state.key === col.key;
        th.setAttribute('aria-sort', on ? (state.dir === 'asc' ? 'ascending' : 'descending') : 'none');
        const ind = th.querySelector('.qo3-sort-ind');
        if (ind) ind.innerHTML = arrowSvg(on ? state.dir : 'none');
      });
      if (select) select.value = state.key ? state.key + '-' + state.dir : '';
      writeState(state);
    }

    function cycle(key) {
      if (state.key !== key) apply({ key, dir: 'asc' });
      else if (state.dir === 'asc') apply({ key, dir: 'desc' });
      else apply({ key: '', dir: '' });
    }

    cols.forEach((col) => {
      const th = header(root, col);
      th.classList.add('qo3-sortable');
      th.setAttribute('tabindex', '0');
      th.setAttribute('aria-sort', 'none');
      th.setAttribute('title', 'Sort by ' + col.label);
      const ind = document.createElement('span');
      ind.className = 'qo3-sort-ind';
      ind.setAttribute('aria-hidden', 'true');
      ind.innerHTML = arrowSvg('none');
      th.appendChild(ind);
      th.addEventListener('click', () => cycle(col.key));
      th.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cycle(col.key); }
      });
    });

    // phones: the table header is hidden at <=768px, so the same choices live in a toolbar dropdown
    const toolbar = root.querySelector('[data-airgrilles-qo3-toolbar]');
    if (toolbar) {
      const label = document.createElement('label');
      label.className = 'airgrilles-qo3__pill airgrilles-qo3__pill--select qo3-sort-pill';
      const span = document.createElement('span');
      span.textContent = 'Sort';
      select = document.createElement('select');
      select.setAttribute('aria-label', 'Sort table');
      select.appendChild(new Option('Default', ''));
      cols.forEach((col) => {
        select.appendChild(new Option(col.label + ': ' + col.asc, col.key + '-asc'));
        select.appendChild(new Option(col.label + ': ' + col.desc, col.key + '-desc'));
      });
      select.addEventListener('change', () => {
        const m = select.value.match(/^([a-z_]+)-(asc|desc)$/);
        apply(m ? { key: m[1], dir: m[2] } : { key: '', dir: '' });
      });
      label.appendChild(span);
      label.appendChild(select);
      const toggle = toolbar.querySelector('[data-airgrilles-qo3-filter-toggle]');
      if (toggle) toggle.insertAdjacentElement('afterend', label);
      else toolbar.appendChild(label);
    }

    const initial = readState();
    if (initial.key) apply(initial);
  }

  function initAll() {
    document.querySelectorAll(ROOT).forEach((root) => {
      try { init(root); } catch (err) { /* never break the table */ }
    });
  }

  // loaded after the QO core script, so its own ordering (size, sold-out last) is already in place
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
